import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useAuth } from '../auth/useAuth';
import { Profile } from '../types';
import { profileApi, SaveProfileInput } from '../api/profileApi';
import { authApi } from '../api/authApi';
import { PROFILE_STORAGE_KEY as STORAGE_KEY, clearAppCache } from '../constants/storage';
import { accountVerificationApi, AccountVerificationStatus } from '../api/accountVerificationApi';

const DEFAULT_PROFILE: Profile = {
  userId: '',
  name: '',
  email: '',
  state: '',
  caddieVoicePreference: 'male',
  caddieTalkativeness: 'succinct',
  caddieVoiceStyle: 'balanced',
  caddieRecommendationDepth: 'standard',
  caddiePrimaryFocus: 'lie',
  measurementSystem: 'imperial',
  handedness: 'right',
  productOfferingId: null,
  isLockedOut: false,
  avatarUrl: '',
  avatarDataUrl: '',
};

interface ProfileContextValue {
  profile: Profile;
  /** Persists the profile (name, state, avatar) to the storage account. */
  saveProfile: (input: SaveProfileInput) => Promise<void>;
  /** Loads avatar bytes on demand and stores them in the browser profile cache. */
  loadAvatar: () => Promise<void>;
  clearProfile: () => void;
  /** Signs the user out of Entra External ID and returns to the app origin. */
  logout: () => void;
  /** True once we've attempted to hydrate the profile from the server after sign-in. */
  isHydrated: boolean;
  /** True when the golfer has selected a membership plan. */
  hasMembership: boolean;
  /** True when CaddieSight staff have locked the account out of the app. */
  isLockedOut: boolean;
  /** Re-fetches the profile from the server. */
  refreshProfile: () => Promise<void>;
  verification: AccountVerificationStatus | null;
  isVerificationHydrated: boolean;
  refreshVerification: () => Promise<AccountVerificationStatus | null>;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

function loadProfile(): Profile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PROFILE;
    return { ...DEFAULT_PROFILE, ...JSON.parse(raw) } as Profile;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return DEFAULT_PROFILE;
  }
}

/**
 * Builds the avatar URL against the client's own API base so a profile saved
 * through one host (e.g. the hosted API) still resolves when viewed through
 * another (e.g. local dev). `remote.avatarUrl` is only used as a has-avatar flag.
 */
function avatarUrlFor(userId: string, remote: { avatarUrl?: string | null; updatedAt: string }): string {
  return remote.avatarUrl ? profileApi.avatarUrl(userId, remote.updatedAt) : '';
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Avatar image could not be read.'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Avatar image could not be read.'));
    reader.readAsDataURL(blob);
  });
}

async function loadAvatarDataUrl(avatarUrl: string): Promise<string> {
  const avatar = await profileApi.getAvatar(avatarUrl);
  return avatar ? blobToDataUrl(avatar) : '';
}

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user: authUser, logout: authLogout } = useAuth();
  const [profile, setProfile] = useState<Profile>(loadProfile);
  // The canonical, storage-safe id for the signed-in user (from `/api/auth/me`).
  const [userId, setUserId] = useState<string>(loadProfile().userId);
  // Whether we've finished the first server hydration attempt after sign-in. The
  // onboarding gate waits on this so returning users aren't briefly routed to onboarding.
  const [isHydrated, setIsHydrated] = useState(false);
  const [verification, setVerification] = useState<AccountVerificationStatus | null>(null);
  const [isVerificationHydrated, setIsVerificationHydrated] = useState(false);

  // Cache to localStorage for a fast first paint / offline fallback.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch {
      // ignore quota / private-mode errors
    }
  }, [profile]);

  // Once signed in, resolve the canonical user id and hydrate the profile from
  // the server so saves made on other devices are reflected here.
  useEffect(() => {
    if (!isAuthenticated) {
      setIsHydrated(false);
      setIsVerificationHydrated(false);
      setVerification(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = authUser?.userId
          ? {
            userId: authUser.userId,
            name: authUser.name,
            email: authUser.email,
          }
          : await authApi.me();
        if (cancelled) return;
        setUserId(me.userId);
        // When a different account signs in on this device, drop the previously cached
        // profile so its data can't briefly show (or persist) under the new user before
        // the server hydration below fills in the real values.
        setProfile((prev) =>
          prev.userId === me.userId
            ? { ...prev, userId: me.userId }
            : {
              ...DEFAULT_PROFILE,
              userId: me.userId,
              name: me.name?.trim() ?? '',
              email: me.email ?? '',
            });

        const [remote, verificationStatus] = await Promise.all([
          profileApi.get(me.userId),
          accountVerificationApi.status(),
        ]);
        if (cancelled) return;
        setVerification(verificationStatus);
        if (remote) {
          const avatarUrl = avatarUrlFor(me.userId, remote);
          setProfile((prev) => ({
            ...prev,
            userId: me.userId,
            name: remote.name,
            email: remote.email ?? '',
            state: remote.state,
            handedness: remote.handedness ?? 'right',
            caddieVoicePreference: remote.caddieVoicePreference ?? 'male',
            caddieTalkativeness: remote.caddieTalkativeness ?? 'succinct',
            caddieVoiceStyle: remote.caddieVoiceStyle ?? 'balanced',
            caddieRecommendationDepth: remote.caddieRecommendationDepth ?? 'standard',
            caddiePrimaryFocus: remote.caddiePrimaryFocus ?? 'lie',
            measurementSystem: remote.measurementSystem ?? 'imperial',
            productOfferingId: remote.productOfferingId ?? null,
            promoCampaign: remote.promoCampaign ?? null,
            isLockedOut: Boolean(remote.isLockedOut),
            avatarUrl,
            avatarDataUrl: remote.avatarUrl && prev.avatarUrl === avatarUrl ? prev.avatarDataUrl : '',
          }));
        } else {
          setProfile({
            ...DEFAULT_PROFILE,
            userId: me.userId,
            name: me.name?.trim() ?? '',
            email: me.email ?? '',
          });
        }
      } catch {
        // Offline / API unavailable — keep the cached profile.
      } finally {
        if (!cancelled) {
          setIsHydrated(true);
          setIsVerificationHydrated(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authUser?.userId, authUser?.name, authUser?.email]);

  const loadAvatar = useCallback(async () => {
    if (!isAuthenticated || !userId) return;
    const expectedAvatarUrl = profile.avatarUrl;
    if (!expectedAvatarUrl || profile.avatarDataUrl) return;
    try {
      const avatarDataUrl = await loadAvatarDataUrl(expectedAvatarUrl);
      setProfile((prev) => (prev.userId === userId && prev.avatarUrl === expectedAvatarUrl
        ? { ...prev, avatarDataUrl }
        : prev));
    } catch {
      // Avatar hydration is optional; keep the direct URL fallback when it is unavailable.
    }
  }, [isAuthenticated, userId, profile.avatarUrl, profile.avatarDataUrl]);

  const saveProfile = useCallback(async (input: SaveProfileInput) => {
    if (!userId) throw new Error('The authenticated user identity is not available yet.');
    const remote = await profileApi.save(userId, input);
    const avatarUrl = avatarUrlFor(userId, remote);
    let avatarDataUrl = '';
    if (remote.avatarUrl) {
      try {
        avatarDataUrl = await loadAvatarDataUrl(avatarUrl);
      } catch {
        // Keep the direct avatar URL as a fallback when the authenticated fetch fails.
      }
    }
    setProfile((prev) => ({
      ...prev,
      userId,
      name: remote.name,
      email: remote.email ?? input.email ?? '',
      state: remote.state,
      handedness: remote.handedness ?? input.handedness ?? prev.handedness,
      caddieVoicePreference: remote.caddieVoicePreference ?? input.caddieVoicePreference,
      caddieTalkativeness: remote.caddieTalkativeness ?? input.caddieTalkativeness,
      caddieVoiceStyle: remote.caddieVoiceStyle ?? input.caddieVoiceStyle,
      caddieRecommendationDepth: remote.caddieRecommendationDepth ?? input.caddieRecommendationDepth,
      caddiePrimaryFocus: remote.caddiePrimaryFocus ?? input.caddiePrimaryFocus,
      measurementSystem: remote.measurementSystem ?? input.measurementSystem,
      productOfferingId: remote.productOfferingId ?? prev.productOfferingId ?? null,
      promoCampaign: remote.promoCampaign ?? prev.promoCampaign ?? null,
      isLockedOut: Boolean(remote.isLockedOut),
      avatarUrl,
      avatarDataUrl,
    }));
    try {
      setVerification(await accountVerificationApi.status());
    } catch {
      // The profile save succeeded. A later route refresh will retry the status check.
    }
  }, [userId]);

  // Re-fetch the profile from the server after account or membership changes.
  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated || !userId) return;
    const remote = await profileApi.get(userId);
    if (!remote) return;
    setProfile((prev) => ({
      ...prev,
      userId,
      name: remote.name,
      email: remote.email ?? prev.email ?? '',
      state: remote.state,
      handedness: remote.handedness ?? prev.handedness,
      productOfferingId: remote.productOfferingId ?? prev.productOfferingId ?? null,
      isLockedOut: Boolean(remote.isLockedOut),
    }));
  }, [isAuthenticated, userId]);

  const refreshVerification = useCallback(async (): Promise<AccountVerificationStatus | null> => {
    if (!isAuthenticated) return null;
    try {
      const status = await accountVerificationApi.status();
      setVerification(status);
      return status;
    } finally {
      setIsVerificationHydrated(true);
    }
  }, [isAuthenticated]);

  const clearProfile = () => setProfile({ ...DEFAULT_PROFILE, userId });

  const logout = useCallback(() => {
    // Wipe every cached CaddieSight value (profile + per-user saved courses) so the
    // next account on this device never sees the previous golfer's data.
    clearAppCache();
    setUserId('');
    setProfile(DEFAULT_PROFILE);
    setVerification(null);
    setIsVerificationHydrated(false);
    authLogout();
  }, [authLogout]);

  const hasMembership = Boolean(profile.productOfferingId);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        saveProfile,
        loadAvatar,
        clearProfile,
        logout,
        isHydrated,
        hasMembership,
        isLockedOut: Boolean(profile.isLockedOut),
        refreshProfile,
        verification,
        isVerificationHydrated,
        refreshVerification,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = (): ProfileContextValue => {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within a ProfileProvider');
  return ctx;
};
